import React from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import ActiveFilters from '../../../components/ui/ActiveFilters';
import { useTranslation } from 'react-i18next';

const FilterBar = ({ filters, onFilterChange, searchQuery, onSearchChange, conferences, resultCount, totalCount }) => {
  const { t } = useTranslation('common');
  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'under_review', label: 'Under Review' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'revision_required', label: 'Revision Required' }
  ];


  const conferenceOptions = [
    { value: 'all', label: 'All Conferences' },
    ...conferences?.map(conf => ({
      value: conf?.id,
      label: conf?.name
    })) || []
  ];

  return (
    <div className="bg-card rounded-lg border border-border p-4 mb-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Search */}
        <div className="md:col-span-2">
          <div className="relative">
            <Icon 
              name="Search" 
              size={18} 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
            />
            <Input
              type="text"
              placeholder="Search by title or keywords..."
              aria-label={t('filters.search', { defaultValue: 'Search' })}
              value={searchQuery}
              onChange={(e) => onSearchChange(e?.target?.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Status Filter */}
        <div>
          <Select
            value={filters?.status}
            onChange={(value) => onFilterChange('status', value)}
            options={statusOptions}
            // No visible label in this compact bar, so the control needs its
            // own accessible name — the current value alone ("All Statuses")
            // tells a screen reader nothing about what it filters.
            aria-label={t('filters.status', { defaultValue: 'Status' })}
          />
        </div>

        {/* Conference Filter */}
        <div>
          <Select
            value={filters?.conference}
            onChange={(value) => onFilterChange('conference', value)}
            options={conferenceOptions}
            aria-label={t('filters.conference', { defaultValue: 'Conference' })}
          />
        </div>
      </div>

      {/* What am I actually filtering by, and how do I undo it. Sits directly
          under the controls that produced it, and disappears when nothing is
          active so the quiet state stays quiet. */}
      <ActiveFilters
        className="mt-3"
        resultCount={resultCount}
        totalCount={totalCount}
        onClearAll={() => {
          onSearchChange('');
          onFilterChange('status', 'all');
          onFilterChange('conference', 'all');
        }}
        filters={[
          searchQuery && {
            key: 'q',
            label: t('filters.search', { defaultValue: 'Search' }),
            value: `"${searchQuery}"`,
            onRemove: () => onSearchChange(''),
          },
          filters?.status && filters.status !== 'all' && {
            key: 'status',
            label: t('filters.status', { defaultValue: 'Status' }),
            value: statusOptions.find((o) => o.value === filters.status)?.label ?? filters.status,
            onRemove: () => onFilterChange('status', 'all'),
          },
          filters?.conference && filters.conference !== 'all' && {
            key: 'conference',
            label: t('filters.conference', { defaultValue: 'Conference' }),
            value: conferenceOptions.find((o) => o.value === filters.conference)?.label ?? filters.conference,
            onRemove: () => onFilterChange('conference', 'all'),
          },
        ].filter(Boolean)}
      />

      {/* The "Sort by" button row lived here. Sorting moved into the table
          headers — same three orderings, plus every other column, without a
          second control competing for the same job. */}
    </div>
  );
};

export default FilterBar;
